// quiz_management/quizlist.js
// Complete component to display available quizzes and student's attempt history
// UPDATED: Added hierarchical filtering (Subject > Chapter > Topic > Subtopic)

import React, { useState, useEffect } from 'react';
import './quizlist.css';
import { authFetch } from '../services/api.js';
import API_BASE_URL from './config';
import EnhancedQuizHistory from './quizenhancedhistory';
import CustomSelect from '../components/CustomSelect';

const QuizList = ({ studentId, onQuizSelect, savedFilters, onFiltersChange }) => {
    const [quizzes, setQuizzes] = useState([]);
    const [studentHistory, setStudentHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const filterType = savedFilters?.filterType || 'all'; // all, attempted, pending
    const [errorType, setErrorType] = useState(null); // 'auth', 'network', 'corruption'

    
    // NEW: Hierarchical filter states
    // Use filters from parent component
    const selectedSubject = savedFilters?.subject || 'all';
    const selectedChapter = savedFilters?.chapter || 'all';
    const selectedTopic = savedFilters?.topic || 'all';
    const selectedSubtopic = savedFilters?.subtopic || 'all';
    
    // NEW: Hierarchical data states
    const [accessibleSubjects, setAccessibleSubjects] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [topics, setTopics] = useState([]);
    const [subtopics, setSubtopics] = useState([]);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedQuizForHistory, setSelectedQuizForHistory] = useState(null);

    useEffect(() => {
        if (studentId) {  // This will now wait for studentId to be set from localStorage
            //console.log('useEffect running, studentId:', studentId);
            loadData();
        }    
    }, [studentId]);

    // Load dependent dropdowns when savedFilters are restored
    useEffect(() => {
        if (savedFilters?.subject && savedFilters.subject !== 'all' && accessibleSubjects.length > 0) {
            console.log('Loading chapters for subject:', savedFilters.subject);
            loadChapters(savedFilters.subject);
        }
    }, [savedFilters?.subject, accessibleSubjects]); // Add accessibleSubjects as dependency
    
    useEffect(() => {
        if (savedFilters?.chapter && savedFilters.chapter !== 'all' && chapters.length > 0) {
            console.log('Loading topics for chapter:', savedFilters.chapter);
            loadTopics(savedFilters.chapter);
        }
    }, [savedFilters?.chapter, chapters]); // Add chapters as dependency
    
    useEffect(() => {
        if (savedFilters?.topic && savedFilters.topic !== 'all' && topics.length > 0) {
            console.log('Loading subtopics for topic:', savedFilters.topic);
            loadSubtopics(savedFilters.topic);
        }
    }, [savedFilters?.topic, topics]); // Add topics as dependency
    

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            setErrorType(null);
            
            // FIXED: Handle multiple possible data structures in localStorage with better error handling
            let accessibleSubjects = [];
            
            try {
                // Try multiple localStorage keys and structures
                const userData = localStorage.getItem('userData');
                const studentData = localStorage.getItem('student');
                
                if (userData) {
                    const parsedData = JSON.parse(userData);
                    
                    // Handle different possible structures
                    if (parsedData.student?.accessible_subjects) {
                        // New structure: userData.student.accessible_subjects
                        accessibleSubjects = parsedData.student.accessible_subjects;
                    } else if (parsedData.accessible_subjects) {
                        // Direct structure: userData.accessible_subjects
                        accessibleSubjects = parsedData.accessible_subjects;
                    }
                } 
                
                // Fallback: try student key
                if (accessibleSubjects.length === 0 && studentData) {
                    const parsedStudent = JSON.parse(studentData);
                    accessibleSubjects = parsedStudent.accessible_subjects || [];
                }
                
            } catch (parseError) {
                console.error('Error parsing localStorage data:', parseError);
                setError('Session data corrupted. Please log in again.');
                setErrorType('corruption');
                return;
            }
            
            // If still no data, show error
            if (!accessibleSubjects || accessibleSubjects.length === 0) {
                console.error('No accessible subjects found in localStorage');
                setError('Please log in again to access quizzes.');
                setErrorType('auth');
                return;
            }
            
            setAccessibleSubjects(accessibleSubjects);
            
            // Load all quizzes
            const quizzesResponse = await authFetch(`${API_BASE_URL}/quiz/quiz/?student_id=${studentId}`);
            if (!quizzesResponse.ok) {
                throw new Error(`HTTP error! status: ${quizzesResponse.status}`);
            }
            const quizzesData = await quizzesResponse.json();
            
            // Filter quizzes to only show those for accessible subjects
            const accessibleSubjectIds = accessibleSubjects.map(s => s.subject_id) || [];
            const filteredQuizzes = quizzesData.filter(quiz => 
                accessibleSubjectIds.includes(quiz.subject_id)
            );
            setQuizzes(filteredQuizzes);
            
            // Load student quiz history
            const historyResponse = await authFetch(`${API_BASE_URL}/quiz/student/${studentId}/quiz-history?student_id=${studentId}`);
            if (historyResponse.ok) {
                const historyData = await historyResponse.json();
                setStudentHistory(historyData);
            }
            
        } catch (err) {
            setError('Failed to load quizzes. Please try again.');
            setErrorType('network');
            console.error('Quiz loading error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Add this function to handle login redirect:
    const handleLoginRedirect = () => {
        // Clear any remaining corrupted data
        localStorage.removeItem('userData');
        localStorage.removeItem('student');
        localStorage.removeItem('token');
        
        // Redirect to login page
        window.location.href = '/login'; // or use navigate('/login') if using React Router
    };
    
    
    // NEW: Load chapters when subject changes
    const loadChapters = async (subjectId) => {
        if (!subjectId || subjectId === 'all') {
            setChapters([]);
            setTopics([]);
            setSubtopics([]);
            return;
        }
        
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/chapters/subject/${subjectId}?student_id=${studentId}`);
            const data = await response.json();
            setChapters(data);
        } catch (err) {
            console.error('Error loading chapters:', err);
            setChapters([]);
        }
        setTopics([]);
        setSubtopics([]);
    };

    // NEW: Load topics when chapter changes
    const loadTopics = async (chapterId) => {
        if (!chapterId || chapterId === 'all') {
            setTopics([]);
            setSubtopics([]);
            return;
        }
        
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/topics/chapter/${chapterId}?student_id=${studentId}`);
            const data = await response.json();
            setTopics(data);
        } catch (err) {
            console.error('Error loading topics:', err);
            setTopics([]);
        }
        setSubtopics([]);
    };

    // NEW: Load subtopics when topic changes
    const loadSubtopics = async (topicId) => {
        if (!topicId || topicId === 'all') {
            setSubtopics([]);
            return;
        }
        
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/subtopics/topic/${topicId}?student_id=${studentId}`);
            const data = await response.json();
            setSubtopics(data);
        } catch (err) {
            console.error('Error loading subtopics:', err);
            setSubtopics([]);
        }
    };

    // NEW: Handle filter changes
    const handleSubjectChange = (subjectId) => {
        onFiltersChange({
            subject: subjectId,
            chapter: 'all',
            topic: 'all',
            subtopic: 'all',
            filterType: filterType  // Add this line to preserve filterType
        });
        loadChapters(subjectId);
    };
    const handleChapterChange = (chapterId) => {
        onFiltersChange({
            subject: selectedSubject,
            chapter: chapterId,
            topic: 'all',
            subtopic: 'all',
            filterType: filterType  // Add this line to preserve filterType
        });
        loadTopics(chapterId);
    };

    const handleTopicChange = (topicId) => {
        onFiltersChange({
            subject: selectedSubject,
            chapter: selectedChapter,
            topic: topicId,
            subtopic: 'all',
            filterType: filterType  // Add this line to preserve filterType
        });
        loadSubtopics(topicId);
    };

    const handleSubtopicChange = (subtopicId) => {
        onFiltersChange({
            subject: selectedSubject,
            chapter: selectedChapter,
            topic: selectedTopic,
            subtopic: subtopicId,
            filterType: filterType  // Add this line to preserve filterType
        });
    };

    const getQuizAttempts = (quizId) => {
        return studentHistory.filter(attempt => attempt.quiz_id === quizId);
    };

    const getBestScore = (quizId) => {
        const attempts = getQuizAttempts(quizId);
        if (attempts.length === 0) return null;
        return Math.max(...attempts.map(attempt => attempt.percentage_score));
    };

    const getLastAttemptDate = (quizId) => {
        const attempts = getQuizAttempts(quizId);
        if (attempts.length === 0) return null;
        const latest = attempts.reduce((latest, current) => 
            new Date(current.created_at) > new Date(latest.created_at) ? current : latest
        );
        return new Date(latest.created_at).toLocaleDateString();
    };

    const getQuizStatus = (quiz) => {
        const attempts = getQuizAttempts(quiz.quiz_id);
        if (attempts.length === 0) return 'pending';
        
        const completed = attempts.filter(attempt => attempt.status === 'completed');
        if (completed.length === 0) return 'in-progress';
        
        return 'attempted';
    };

    const filteredQuizzes = quizzes.filter(quiz => {
        // Filter by subject
        if (selectedSubject !== 'all' && quiz.subject_id !== parseInt(selectedSubject)) {
            return false;
        }
        
        // Filter by chapter
        if (selectedChapter !== 'all' && quiz.chapter_id !== parseInt(selectedChapter)) {
            return false;
        }
        
        // Filter by topic
        if (selectedTopic !== 'all' && quiz.topic_id !== parseInt(selectedTopic)) {
            return false;
        }
        
        // Filter by subtopic
        if (selectedSubtopic !== 'all' && quiz.subtopic_id !== parseInt(selectedSubtopic)) {
            return false;
        }
        
        // Filter by attempt status
        if (filterType === 'attempted') {
            return getQuizStatus(quiz) === 'attempted';
        } else if (filterType === 'pending') {
            return getQuizStatus(quiz) === 'pending';
        }
        
        return true;
    });

    const formatTime = (minutes) => {
        if (!minutes) return 'No time limit';
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    const getStatusBadge = (quiz) => {
        const status = getQuizStatus(quiz);
        const badgeClass = {
            'pending': 'badge-pending',
            'attempted': 'badge-attempted',
            'in-progress': 'badge-progress'
        }[status];
        
        const badgeText = {
            'pending': 'Not Started',
            'attempted': 'Completed',
            'in-progress': 'In Progress'
        }[status];
        
        return <span className={`status-badge ${badgeClass}`}>{badgeText}</span>;
    };

    if (loading) {
        return (
            <div className="quiz-list-container">
                <div className="loading-spinner">Loading quizzes...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="quiz-list-container">
                <div className="error-message">
                    {error}
                    
                    {/* Show different buttons based on error type */}
                    {errorType === 'auth' || errorType === 'corruption' ? (
                        <button 
                            className="login-redirect-btn" 
                            onClick={handleLoginRedirect}
                            style={{
                                marginTop: '10px',
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Go to Login
                        </button>
                    ) : (
                        <button className="retry-btn" onClick={loadData}>
                            Retry
                        </button>
                    )}
                </div>
            </div>
        );
    }

    
        
    return (
        <>
            <div className="quiz-list-container">
                <div className="quiz-list-header">
                    <h1>Available Quizzes</h1>
                    <p>Select a quiz to start or continue your practice</p>
                </div>

                <div className="quiz-filters">
                    <div className="filter-group">
                        <label>Filter by Status:</label>
                        <CustomSelect
                            value={filterType}
                            onChange={(e) => onFiltersChange({
                                subject: selectedSubject,
                                chapter: selectedChapter,
                                topic: selectedTopic,
                                subtopic: selectedSubtopic,
                                filterType: e.target.value
                            })}
                            className="filter-select"
                            options={[
                                { value: 'all', label: 'All Quizzes' },
                                { value: 'pending', label: 'Not Started' },
                                { value: 'attempted', label: 'Completed' },
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Filter by Subject:</label>
                        <CustomSelect
                            value={selectedSubject}
                            onChange={(e) => handleSubjectChange(e.target.value)}
                            className="filter-select"
                            options={[
                                { value: 'all', label: 'All Subjects' },
                                ...accessibleSubjects.map(s => ({ value: s.subject_id, label: s.subject_name }))
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Filter by Chapter:</label>
                        <CustomSelect
                            value={selectedChapter}
                            onChange={(e) => handleChapterChange(e.target.value)}
                            className="filter-select"
                            disabled={selectedSubject === 'all'}
                            options={[
                                { value: 'all', label: 'All Chapters' },
                                ...chapters.map(c => ({ value: c.chapter_id, label: c.name }))
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Filter by Topic:</label>
                        <CustomSelect
                            value={selectedTopic}
                            onChange={(e) => handleTopicChange(e.target.value)}
                            className="filter-select"
                            disabled={selectedChapter === 'all'}
                            options={[
                                { value: 'all', label: 'All Topics' },
                                ...topics.map(t => ({ value: t.topic_id, label: t.name }))
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Filter by Subtopic:</label>
                        <CustomSelect
                            value={selectedSubtopic}
                            onChange={(e) => handleSubtopicChange(e.target.value)}
                            className="filter-select"
                            disabled={selectedTopic === 'all'}
                            options={[
                                { value: 'all', label: 'All Subtopics' },
                                ...subtopics.map(s => ({ value: s.subtopic_id, label: s.name }))
                            ]}
                        />
                    </div>
                </div>

                <div className="quiz-stats">
                    <div className="stat-card">
                        <h3>{quizzes.length}</h3>
                        <p>Total Quizzes</p>
                    </div>
                    <div className="stat-card">
                        <h3>{quizzes.filter(quiz => getQuizStatus(quiz) === 'attempted').length}</h3>
                        <p>Completed</p>
                    </div>
                    <div className="stat-card">
                        <h3>{quizzes.filter(quiz => getQuizStatus(quiz) === 'pending').length}</h3>
                        <p>Pending</p>
                    </div>
                    <div className="stat-card">
                        <h3>{studentHistory.length}</h3>
                        <p>Total Attempts</p>
                    </div>
                </div>

                <div className="quiz-grid">
                    {filteredQuizzes.map(quiz => {
                        const attempts = getQuizAttempts(quiz.quiz_id);
                        const bestScore = getBestScore(quiz.quiz_id);
                        const lastAttemptDate = getLastAttemptDate(quiz.quiz_id);
                        
                        return (
                            <div key={quiz.quiz_id} className="quiz-card">
                                <div className="quiz-card-header">
                                    <h3>{quiz.quiz_title}</h3>
                                    {getStatusBadge(quiz)}
                                </div>
                                
                                <div className="quiz-card-body">
                                    <p className="quiz-description">
                                        {quiz.quiz_description || 'No description available'}
                                    </p>
                                    
                                    <div className="quiz-details">
                                        <div className="detail-item">
                                            <span className="detail-label">Questions:</span>
                                            <span className="detail-value">{quiz.total_questions}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Time Limit:</span>
                                            <span className="detail-value">{formatTime(quiz.time_limit_minutes)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Type:</span>
                                            <span className="detail-value quiz-type">{quiz.quiz_type}</span>
                                        </div>
                                    </div>

                                    {attempts.length > 0 && (
                                        <div className="attempt-history">
                                            <h4>Your Progress</h4>
                                            <div className="progress-stats">
                                                <div className="progress-item">
                                                    <span className="progress-label">Attempts:</span>
                                                    <span className="progress-value">{attempts.length}</span>
                                                </div>
                                                {bestScore !== null && (
                                                    <div className="progress-item">
                                                        <span className="progress-label">Best Score:</span>
                                                        <span className={`progress-value score ${bestScore >= 80 ? 'high' : bestScore >= 60 ? 'medium' : 'low'}`}>
                                                            {bestScore.toFixed(1)}%
                                                        </span>
                                                    </div>
                                                )}
                                                {lastAttemptDate && (
                                                    <div className="progress-item">
                                                        <span className="progress-label">Last Attempt:</span>
                                                        <span className="progress-value">{lastAttemptDate}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="quiz-card-footer">
                                    <button 
                                        className="start-quiz-btn"
                                        onClick={() => onQuizSelect(quiz.quiz_id)}
                                    >
                                        {attempts.length > 0 ? 'Reattempt Quiz' : 'Start Quiz'}
                                    </button>
                                    
                                    {attempts.length > 0 && (
                                        <button 
                                            className="view-history-btn"
                                            onClick={() => {
                                                console.log('View History clicked! Quiz ID:', quiz.quiz_id);
                                                setSelectedQuizForHistory(quiz.quiz_id);
                                                setShowHistoryModal(true);
                                                console.log('Modal state set to true');
                                            }}
                                        >
                                            View History
                                        </button>                                )}
                                </div>
                            </div>
                        );
                    })}
                </div>  {/* This closes quiz-grid */}

                
            {filteredQuizzes.length === 0 && (
                <div className="no-quizzes">
                    <h3>No quizzes found</h3>
                    <p>Try adjusting your filters or check back later for new quizzes.</p>
                </div>
            )}
        
        </div>  {/* This closes quiz-list-container */}
        
        {/* History Modal - NOW OUTSIDE THE CONTAINER */}
        {showHistoryModal && (
            <div className="history-modal-overlay" style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center'}} onClick={() => setShowHistoryModal(false)}>
                <div className="history-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="history-modal-header">
                        <h3>Quiz Attempt History</h3>
                        <button 
                            className="history-modal-close"
                            onClick={() => setShowHistoryModal(false)}
                        >
                            ×
                        </button>
                    </div>
                    <div className="history-modal-body">
                        <EnhancedQuizHistory 
                            studentId={studentId} 
                            onQuizSelect={onQuizSelect}
                            specificQuizId={selectedQuizForHistory}
                        />
                    </div>
                </div>
            </div>
        )}
    </>  
    );
};

export default QuizList;